import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  fetchOpsDashboard,
  liveAction,
  runAutomation,
  materializeSchedule,
  fetchAnalyticsSummary,
  fetchPlayoutClock,
} from "./channelScheduleApi";
import "./BroadcastStudio.css";
import "./BroadcastOpsPage.css";

const ACTIONS = [
  { id: "skip", label: "Skip" },
  { id: "previous", label: "Previous" },
  { id: "extend", label: "Extend +15m", params: { minutes: 15 } },
  { id: "shorten", label: "Shorten −5m", params: { minutes: 5 } },
  { id: "pause_playout", label: "Pause playout" },
  { id: "resume_playout", label: "Resume playout" },
  { id: "intermission", label: "Intermission" },
  { id: "trigger_ad", label: "Trigger Ad" },
  { id: "force_break", label: "Force Ad Break" },
  { id: "station_id", label: "Station ID" },
  { id: "promo_break", label: "Promo Break" },
  { id: "start_live", label: "Start Live" },
  { id: "end_live", label: "End Live" },
  { id: "pause_automation", label: "Pause Auto" },
  { id: "resume_automation", label: "Resume Auto" },
  { id: "emergency_cut", label: "Emergency Cut" },
  { id: "resume_schedule", label: "Resume Schedule" },
];

/**
 * Live Broadcast Operations Dashboard.
 */
function BroadcastOpsPage() {
  const [dash, setDash] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [clock, setClock] = useState(null);
  const [jumpEntryId, setJumpEntryId] = useState("");

  const refresh = useCallback(async () => {
    const data = await fetchOpsDashboard({});
    setDash(data);
    if (data && data.channels && data.channels.length && !selected) {
      setSelected(data.channels[0].channel.id);
    }
    const summary = await fetchAnalyticsSummary({ hours: 24 });
    setAnalytics(summary);
    if (selected) {
      const tick = await fetchPlayoutClock({ channel_id: selected });
      setClock(tick);
    }
  }, [selected]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  const onAction = async (action, params) => {
    if (!selected) return;
    setBusy(true);
    setMessage("");
    try {
      const result = await liveAction(
        Object.assign({ channel_id: selected, action: action }, params || {})
      );
      setMessage(result.success ? "Applied: " + action : result.error || "Failed");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const rows = (dash && dash.channels) || [];
  const active = rows.find((r) => r.channel && r.channel.id === selected);

  return (
    <div className="bcast-studio ops-page">
      <Helmet>
        <title>Live Operations · TPG Broadcast</title>
      </Helmet>
      <header className="bcast-studio__header">
        <div>
          <p className="bcast-studio__eyebrow">Broadcast Control Center</p>
          <h1>Live Operations</h1>
        </div>
        <nav className="bcast-studio__nav">
          <Link to="/studio/timeline">Timeline Studio</Link>
          <Link to="/guide">EPG</Link>
        </nav>
      </header>

      <div className="ops-toolbar">
        <button type="button" className="bcast-btn" onClick={refresh} disabled={busy}>
          Refresh
        </button>
        <button
          type="button"
          className="bcast-btn bcast-btn--ghost"
          onClick={() => runAutomation({}).then(refresh)}
        >
          Run automation
        </button>
        <button
          type="button"
          className="bcast-btn bcast-btn--ghost"
          onClick={() => materializeSchedule({ days: 7 }).then(refresh)}
        >
          Materialize schedules
        </button>
        {message && <span className="ops-message">{message}</span>}
      </div>

      {analytics && (
        <div className="ops-analytics">
          <span>
            Tunes (24h): <strong>{analytics.tunes}</strong>
          </span>
          <span>
            Switches: <strong>{analytics.channel_switches}</strong>
          </span>
        </div>
      )}

      <div className="ops-layout">
        <div className="ops-channels">
          {rows.map((row) => {
            const ch = row.channel;
            const health = (row.health && row.health.status) || "ok";
            return (
              <button
                type="button"
                key={ch.id}
                className={
                  "ops-channel" + (selected === ch.id ? " is-active" : "") + " is-" + health
                }
                onClick={() => setSelected(ch.id)}
              >
                <strong>{ch.name}</strong>
                <span>{row.phase || "—"}</span>
                <span className="ops-channel__program">
                  {(row.current && row.current.title) || "Off air / slate"}
                </span>
              </button>
            );
          })}
        </div>

        <div className="ops-detail">
          {!active && <p className="bcast-studio__status">Select a channel.</p>}
          {active && (
            <>
              <h2>{active.channel.name}</h2>
              <dl className="ops-dl">
                <div>
                  <dt>Phase</dt>
                  <dd>{active.phase}</dd>
                </div>
                <div>
                  <dt>Clock</dt>
                  <dd>
                    {(clock && clock.clock && clock.clock.clock_state) || "—"}
                    {clock && clock.clock && clock.clock.remaining_seconds != null
                      ? " · rem " +
                        Math.max(0, clock.clock.remaining_seconds) +
                        "s"
                      : ""}
                  </dd>
                </div>
                <div>
                  <dt>Now</dt>
                  <dd>{(active.current && active.current.title) || "—"}</dd>
                </div>
                <div>
                  <dt>Next</dt>
                  <dd>{(active.next && active.next.title) || "—"}</dd>
                </div>
                <div>
                  <dt>Previous</dt>
                  <dd>
                    {(clock && clock.previous && clock.previous.title) || "—"}
                  </dd>
                </div>
                <div>
                  <dt>Loop</dt>
                  <dd>
                    {(clock && clock.loop && clock.loop.loop_mode) || "one_time"}
                    {clock && clock.loop && clock.loop.loop_paused
                      ? " · paused"
                      : ""}
                  </dd>
                </div>
                <div>
                  <dt>Media Asset</dt>
                  <dd>
                    {(active.media_asset && active.media_asset.title) || "—"}
                    <small>
                      {(active.media_asset && active.media_asset.id) || ""}
                    </small>
                  </dd>
                </div>
                <div>
                  <dt>Advertising</dt>
                  <dd>
                    {(active.advertising && active.advertising.policy) || "—"}
                    {active.advertising && active.advertising.use_broadcast_breaks
                      ? " · broadcast breaks"
                      : ""}
                    {active.advertising && active.advertising.use_media_asset_avod
                      ? " · media AVOD"
                      : ""}
                  </dd>
                </div>
                <div>
                  <dt>Automation</dt>
                  <dd>
                    {active.ops && active.ops.automation_paused ? "Paused" : "Running"}
                    {active.ops && active.ops.live_takeover_active
                      ? " · LIVE TAKEOVER"
                      : ""}
                    {active.ops && active.ops.emergency_active ? " · EMERGENCY" : ""}
                  </dd>
                </div>
                <div>
                  <dt>Outputs</dt>
                  <dd>
                    {clock && clock.outputs && clock.outputs.length
                      ? clock.outputs
                          .map(function (o) {
                            return o.name + " (" + o.status + ")";
                          })
                          .join(", ")
                      : "None configured"}
                    <small> · encoder worker deferred</small>
                  </dd>
                </div>
                <div>
                  <dt>Health</dt>
                  <dd>{(active.health && active.health.status) || "ok"}</dd>
                </div>
              </dl>

              <div className="ops-actions">
                {ACTIONS.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="bcast-btn bcast-btn--ghost"
                    disabled={busy}
                    onClick={() => onAction(a.id, a.params)}
                  >
                    {a.label}
                  </button>
                ))}
                <div className="ops-jump">
                  <input
                    type="text"
                    placeholder="Jump entry ID"
                    value={jumpEntryId}
                    onChange={(e) => setJumpEntryId(e.target.value)}
                  />
                  <button
                    type="button"
                    className="bcast-btn"
                    disabled={busy || !jumpEntryId}
                    onClick={() =>
                      onAction("jump", { entry_id: jumpEntryId })
                    }
                  >
                    Jump
                  </button>
                </div>
                <Link
                  className="bcast-btn"
                  to={"/channel/" + (active.channel.slug || active.channel.unique_id)}
                >
                  Tune channel
                </Link>
              </div>
            </>
          )}
        </div>

        <aside className="ops-alerts">
          <h3>Alerts</h3>
          <ul>
            {((dash && dash.open_alerts) || []).map((a) => (
              <li key={a.id} className={"ops-alert is-" + a.severity}>
                <strong>{a.title}</strong>
                <span>{a.message}</span>
              </li>
            ))}
            {!(dash && dash.open_alerts && dash.open_alerts.length) && (
              <li className="ops-alert">No open alerts</li>
            )}
          </ul>
        </aside>
      </div>
    </div>
  );
}

export default BroadcastOpsPage;
